import { useMemo, useState } from "react";
import styled from "@emotion/styled";
import Lowlight from "react-lowlight";
import { Link } from "react-router-dom";

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export interface TimelineEvent {
  id: string;
  kind: string;
  occurred_at: string;
  properties_json: Record<string, unknown>;
  summary?: string;
}

export interface SpecialProperty {
  key: string;
  label: string;
  id: string;
  route?: string;
  href?: string;
  external?: boolean;
}

export interface EventTimelineProps<T extends TimelineEvent> {
  events: T[];
  total: number;
  isLoading?: boolean;
  isError?: boolean;
  createdAt?: string;
  createdLabel?: string;
  emptyMessage?: string;
  getSpecialProperties?: (properties: Record<string, unknown>) => SpecialProperty[];
  sortDirection?: "asc" | "desc";
  title?: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Styled Components
// ────────────────────────────────────────────────────────────────────────────

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EventList = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding-left: 32px;
`;

const TimelineNodeWrapper = styled.div`
  position: absolute;
  left: -32px;
  top: -4px;
  bottom: -4px;
  width: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TimelineConnectorTop = styled.div`
  width: 2px;
  flex: 1;
  background: #e2e8f0;
`;

const TimelineConnectorBottom = styled.div`
  width: 2px;
  flex: 1;
  background: #e2e8f0;
`;

const TimelineNodeDot = styled.div<{ eventType: string; isSpecial?: boolean }>`
  width: ${({ isSpecial }) => (isSpecial ? "14px" : "10px")};
  height: ${({ isSpecial }) => (isSpecial ? "14px" : "10px")};
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ eventType, isSpecial }) => {
    if (isSpecial) return "#8b5cf6";
    if (eventType.includes("start")) return "#3b82f6";
    if (eventType.includes("complete")) return "#22c55e";
    if (eventType.includes("fail")) return "#ef4444";
    if (eventType.includes("created")) return "#8b5cf6";
    return "#94a3b8";
  }};
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px
    ${({ eventType, isSpecial }) => {
      if (isSpecial) return "#ede9fe";
      if (eventType.includes("start")) return "#dbeafe";
      if (eventType.includes("complete")) return "#dcfce7";
      if (eventType.includes("fail")) return "#fee2e2";
      if (eventType.includes("created")) return "#ede9fe";
      return "#f1f5f9";
    }};
  z-index: 1;
`;

const TimelineNode = ({
  eventType,
  isSpecial,
  isFirst,
  isLast,
}: {
  eventType: string;
  isSpecial?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}) => (
  <TimelineNodeWrapper>
    {!isFirst ? <TimelineConnectorTop /> : <div style={{ flex: 1 }} />}
    <TimelineNodeDot eventType={eventType} isSpecial={isSpecial} />
    {!isLast ? <TimelineConnectorBottom /> : <div style={{ flex: 1 }} />}
  </TimelineNodeWrapper>
);

const EventRow = styled.div<{ isExpanded?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: ${({ isExpanded }) => (isExpanded ? "#f8fafc" : "#fff")};
  border: 1px solid ${({ isExpanded }) => (isExpanded ? "#cbd5e1" : "#e2e8f0")};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  min-height: 84px;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const EventTime = styled.span`
  font-size: 12px;
  color: #64748b;
  flex-shrink: 0;
`;

const EventDivider = styled.span`
  color: #cbd5e1;
  font-size: 12px;
`;

const EventDetails = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
  padding-right: 8px;
`;

const EventKind = styled.span<{ eventType: string }>`
  font-size: 13px;
  font-weight: 500;
  color: ${({ eventType }) => {
    if (eventType.includes("start")) return "#1d4ed8";
    if (eventType.includes("complete")) return "#166534";
    if (eventType.includes("fail")) return "#dc2626";
    if (eventType.includes("created")) return "#7c3aed";
    return "#374151";
  }};
`;

const EventSummary = styled.span`
  font-size: 12px;
  color: #64748b;
  min-width: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.4;
  max-height: calc(1.4em * 3);
  max-width: 100%;
`;

const ExpandIcon = styled.span<{ isExpanded: boolean }>`
  font-size: 10px;
  color: #94a3b8;
  transition: transform 0.15s ease;
  transform: rotate(${({ isExpanded }) => (isExpanded ? "180deg" : "0deg")});
`;

const PropertiesBlock = styled.div`
  margin-top: 8px;
  margin-left: 0;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  font-size: 11px;
  background: #fff;
`;

const SpecialPropertiesBlock = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SpecialPropertyLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
  border-radius: 6px;
  font-size: 12px;
  color: #1d4ed8;
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    border-color: #60a5fa;
  }
`;

const SpecialPropertyAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
  border-radius: 6px;
  font-size: 12px;
  color: #1d4ed8;
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    border-color: #60a5fa;
  }
`;

const SpecialPropertyLabel = styled.span`
  font-weight: 500;
`;

const SpecialPropertyId = styled.span`
  font-family: "Berkeley Mono Variable", monospace;
  font-size: 11px;
  opacity: 0.8;
`;

const EventCard = styled.div`
  position: relative;
  margin: 4px 0;
  padding-left: 0;
`;

const CreatedEvent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
  border: 1px solid #d8b4fe;
  border-radius: 8px;
`;

const CreatedLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #7c3aed;
`;

const CreatedTime = styled.span`
  font-size: 12px;
  color: #8b5cf6;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #e2e8f0;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 8px;
`;

const TotalCount = styled.span`
  font-size: 12px;
  color: #64748b;
  font-weight: 400;
`;

// ────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ────────────────────────────────────────────────────────────────────────────

export const formatEventKind = (kind: string): string => {
  return kind.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const dayName = days[date.getDay()];
  const monthName = months[date.getMonth()];
  const dayNum = date.getDate();
  const ordinal = getOrdinalSuffix(dayNum);
  const time = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });

  return `${dayName}, ${monthName} ${dayNum}${ordinal} at ${time}`;
};

const formatFullDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString();
};

// Filter out special properties from the JSON display
const filterSpecialProperties = (properties: Record<string, unknown>, specialKeys: string[]) => {
  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!specialKeys.includes(key) || !value) {
      filtered[key] = value;
    }
  }
  return filtered;
};

// ────────────────────────────────────────────────────────────────────────────
// Event Item Component
// ────────────────────────────────────────────────────────────────────────────

interface EventItemProps<T extends TimelineEvent> {
  event: T;
  isExpanded: boolean;
  onToggle: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  getSpecialProperties?: (properties: Record<string, unknown>) => SpecialProperty[];
}

function EventItem<T extends TimelineEvent>({
  event,
  isExpanded,
  onToggle,
  isFirst,
  isLast,
  getSpecialProperties,
}: EventItemProps<T>) {
  const formattedKind = formatEventKind(event.kind);
  const formattedTime = formatDateTime(event.occurred_at);
  const hasProperties = event.properties_json && Object.keys(event.properties_json).length > 0;
  const summaryText = event.summary?.trim();

  const specialProperties =
    hasProperties && getSpecialProperties ? getSpecialProperties(event.properties_json) : [];
  const specialKeys = specialProperties.map(p => p.key);
  const filteredProperties = hasProperties
    ? filterSpecialProperties(event.properties_json, specialKeys)
    : {};
  const hasFilteredProperties = Object.keys(filteredProperties).length > 0;

  return (
    <EventCard>
      <TimelineNode eventType={event.kind} isFirst={isFirst} isLast={isLast} />
      <EventRow isExpanded={isExpanded} onClick={onToggle}>
        <EventTime title={formatFullDate(event.occurred_at)}>{formattedTime}</EventTime>
        <EventDivider>-</EventDivider>
        <EventDetails>
          <EventKind eventType={event.kind}>{formattedKind}</EventKind>
          {summaryText ? <EventSummary title={summaryText}>{summaryText}</EventSummary> : null}
        </EventDetails>
        {hasProperties && <ExpandIcon isExpanded={isExpanded}>&#x25BC;</ExpandIcon>}
      </EventRow>
      {isExpanded && hasProperties && (
        <>
          {specialProperties.length > 0 && (
            <SpecialPropertiesBlock>
              {specialProperties.map(prop =>
                prop.href ? (
                  <SpecialPropertyAnchor
                    key={prop.key}
                    href={prop.href}
                    target={prop.external ? "_blank" : undefined}
                    rel={prop.external ? "noreferrer" : undefined}
                    onClick={e => e.stopPropagation()}
                  >
                    <SpecialPropertyLabel>View {prop.label}:</SpecialPropertyLabel>
                    <SpecialPropertyId>{prop.id.slice(0, 8)}...</SpecialPropertyId>
                  </SpecialPropertyAnchor>
                ) : (
                  <SpecialPropertyLink
                    key={prop.key}
                    to={prop.route ?? "#"}
                    onClick={e => e.stopPropagation()}
                  >
                    <SpecialPropertyLabel>View {prop.label}:</SpecialPropertyLabel>
                    <SpecialPropertyId>{prop.id.slice(0, 8)}...</SpecialPropertyId>
                  </SpecialPropertyLink>
                )
              )}
            </SpecialPropertiesBlock>
          )}
          {hasFilteredProperties && (
            <PropertiesBlock>
              <Lowlight
                language="json"
                value={JSON.stringify(filteredProperties, null, 2)}
                markers={[]}
              />
            </PropertiesBlock>
          )}
        </>
      )}
    </EventCard>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Created Event Item Component
// ────────────────────────────────────────────────────────────────────────────

const CreatedEventItem = ({
  createdAt,
  label,
  isFirst,
  isLast,
}: {
  createdAt: string;
  label: string;
  isFirst?: boolean;
  isLast?: boolean;
}) => {
  return (
    <EventCard>
      <TimelineNode eventType="" isSpecial isFirst={isFirst} isLast={isLast} />
      <CreatedEvent>
        <CreatedTime title={formatFullDate(createdAt)}>{formatDateTime(createdAt)}</CreatedTime>
        <EventDivider>-</EventDivider>
        <CreatedLabel>{label}</CreatedLabel>
      </CreatedEvent>
    </EventCard>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Main Timeline Component
// ────────────────────────────────────────────────────────────────────────────

export function EventTimeline<T extends TimelineEvent>({
  events,
  total,
  isLoading,
  isError,
  createdAt,
  createdLabel = "Created",
  emptyMessage = "No events recorded yet.",
  getSpecialProperties,
  sortDirection = "asc",
  title = "Event Timeline",
}: EventTimelineProps<T>) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Sort events by occurred_at
  const sortedEvents = useMemo(() => {
    const ordered = [...events].sort(
      (a, b) => new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime()
    );
    return sortDirection === "desc" ? ordered.reverse() : ordered;
  }, [events, sortDirection]);

  if (isLoading) {
    return (
      <TimelineContainer>
        <Label>{title}</Label>
        <EmptyState>Loading events...</EmptyState>
      </TimelineContainer>
    );
  }

  if (isError) {
    return (
      <TimelineContainer>
        <Label>{title}</Label>
        <EmptyState>Failed to load events</EmptyState>
      </TimelineContainer>
    );
  }

  const eventCount = createdAt ? total + 1 : total;

  return (
    <TimelineContainer>
      <TimelineHeader>
        <Label>
          {title} <TotalCount>({eventCount} events)</TotalCount>
        </Label>
      </TimelineHeader>
      {events.length === 0 && !createdAt ? (
        <EmptyState>{emptyMessage}</EmptyState>
      ) : (
        <EventList>
          {createdAt && (
            <CreatedEventItem
              createdAt={createdAt}
              label={createdLabel}
              isFirst
              isLast={sortedEvents.length === 0}
            />
          )}
          {sortedEvents.map((event, index) => (
            <EventItem
              key={event.id}
              event={event}
              isExpanded={expandedIds.has(event.id)}
              onToggle={() => toggleExpanded(event.id)}
              isFirst={!createdAt && index === 0}
              isLast={index === sortedEvents.length - 1}
              getSpecialProperties={getSpecialProperties}
            />
          ))}
        </EventList>
      )}
    </TimelineContainer>
  );
}
